import { useCloseOnOverlayClick, useModalModel } from '@workday/canvas-kit-react/modal';
import {
  useAssistiveHideSiblings,
  useCloseOnEscape,
  useDisableBodyScroll,
  useFocusTrap,
  usePopupModel,
  useReturnFocus,
} from '@workday/canvas-kit-react/popup';
import { system } from '@workday/canvas-tokens-web';
import React from 'react';
import { TOGGLE_BLOCKING_POPUPS } from 'config/toggles-builder';
import { DevsiteModalHeading, DevsiteModalLowEmphasis, ModalOptions, ModalWidth, ModelOptions } from '.';
import { Canvas } from '../../Canvas';
import { useOptionalInitialFocus } from './useOptionalInitialFocus';

// reused in each of our modal types
type ModalFC = React.FC<React.PropsWithChildren<{ closeAll: () => void; options?: ModalOptions }>>;

// canvas doesn't expose this type directly
type ModalModel = ReturnType<typeof useModalModel>;

// used to check that we can pass a model on to the child component
type CanHaveModel = { model?: ModalModel };

export const PopupContextModal: ModalFC = (props) =>
  TOGGLE_BLOCKING_POPUPS && props.options?.blocking ? <BlockingModal {...props} /> : <NonBlockingModal {...props} />;

const BlockingModal: ModalFC = ({ children, closeAll, options }) => {
  const model = useBlockingModalModel(options?.modelOptions, closeAll);

  return (
    <ModalContent model={model} options={options}>
      {children}
    </ModalContent>
  );
};

const NonBlockingModal: ModalFC = ({ children, closeAll, options }) => {
  const model = useNonBlockingModalModel(options?.modelOptions, closeAll);

  return (
    <ModalContent model={model} options={options}>
      {children}
    </ModalContent>
  );
};

const ModalContent: React.FC<
  React.PropsWithChildren<{
    model: ModalModel;
    options?: ModalOptions;
  }>
> = ({ children, model, options }) => (
  <Canvas.Modal model={model}>
    <Canvas.Modal.OverflowOverlay>
      <Canvas.Modal.Card
        overflow={options?.overflow}
        padding={options?.padding || system.space.x6}
        width={options?.width || ModalWidth.s}
        height={options?.height}
        data-cy='modal-card'
        maxHeight={options?.maxHeight || '80vh'}
        transition='width ease-in-out 300ms'
        backgroundColor={options?.cardBackgroundColor}
      >
        {options?.heading &&
          (typeof options.heading === 'string' ? (
            <DevsiteModalHeading>
              {options.heading}
              {options.headingLowEmphasis && (
                <DevsiteModalLowEmphasis> {options.headingLowEmphasis}</DevsiteModalLowEmphasis>
              )}
            </DevsiteModalHeading>
          ) : (
            options.heading
          ))}
        {!options?.blocking && !options?.hideDefaultCloseIcon && (
          <Canvas.Modal.CloseIcon data-cy='modal-close-icon' aria-label='Close' />
        )}
        {/**
         * Cloning `children` elements in order to dynamically attach the instantiated model as a prop.
         *
         * See CanvasKit documentation:
         *
         * The model is necessary if we want to trigger an `onHide` event when the modal is closed
         * because the only way to trigger the model's `onHide` method is by triggering the model's underlying state:
         *  `model.events.hide()`
         *   -> causes model state change
         *    -> triggers `onHide()` method
         *
         * */}
        {React.Children.map(children, (child: React.ReactElement) => {
          // Add a check so that we only try to add the passed model
          // to a valid ReactElement, with typing narrowed appropriately.
          if (!React.isValidElement<CanHaveModel>(child)) return child;
          return React.cloneElement(child, { model });
        })}
      </Canvas.Modal.Card>
    </Canvas.Modal.OverflowOverlay>
  </Canvas.Modal>
);

const useBlockingModalModel = (modelOptions: ModelOptions | undefined, closeAll: () => void) => {
  const model = usePopupModel({
    initialVisibility: 'visible',
    ...modelOptions,
    onHide: () => {
      modelOptions?.onHide?.();
      closeAll();
    },
  });

  // Apply only the essential hooks for blocking modals - NO close behaviors
  useOptionalInitialFocus(model);
  useReturnFocus(model);
  useFocusTrap(model);
  useAssistiveHideSiblings(model);
  useDisableBodyScroll(model);

  return model;
};

const useNonBlockingModalModel = (modelOptions: ModelOptions | undefined, closeAll: () => void) => {
  // Use the blocking modal model as a base
  const model = useBlockingModalModel(modelOptions, closeAll);

  // Add close behavior hooks
  useCloseOnEscape(model);
  useCloseOnOverlayClick(model);

  return model;
};
